export type ActivityType = 'call' | 'email' | 'event' | 'note'
export type CallDisposition =
  | 'Connected'
  | 'Voicemail'
  | 'No Answer'
  | 'Busy'
  | 'Left Message'
export type EmailStatus = 'sent' | 'opened' | 'bounced' | 'clicked'

export interface Activity {
  id: string
  type: ActivityType
  timestamp: string // ISO string
  summary: string
  details?: string

  // Call specific
  duration?: number // in seconds
  disposition?: CallDisposition
  isMeaningful?: boolean

  // Email specific
  emailStatus?: EmailStatus

  // Event specific
  eventType?: string

  // Note specific
  author?: string
}

export interface BorrowerMetrics {
  lastMeaningfulContact: string | null // ISO string
  lastCallDisposition: CallDisposition | null
  lastCallDuration: number | null // in seconds
  emailsSent: number
  emailsOpened: number
}

// Helper to format dates for mock data relative to today
const today = new Date()
const subtractDays = (days: number, hours: number = 0, minutes: number = 0) => {
  const d = new Date(today)
  d.setDate(d.getDate() - days)
  d.setHours(d.getHours() - hours)
  d.setMinutes(d.getMinutes() - minutes)
  return d.toISOString()
}

export const mockActivities: Activity[] = [
  {
    id: 'act-1',
    type: 'call',
    timestamp: subtractDays(0, 2, 15), // 2 hours 15 mins ago
    summary: 'Outbound Call - Follow up on W-2 submission',
    details:
      'Spoke with Sarah regarding her 2023 W-2s. She mentioned she had trouble downloading them from her employer portal but will try again tonight. Walked her through the alternative document upload process just in case. Very engaged and eager to close.',
    duration: 752, // 12m 32s
    disposition: 'Connected',
    isMeaningful: true,
  },
  {
    id: 'act-2',
    type: 'note',
    timestamp: subtractDays(0, 4, 0), // 4 hours ago
    summary: 'Internal Note: Rate Lock Expiring Soon',
    details:
      "Sarah's current rate lock of 6.25% expires in 12 days. We need to ensure all conditions are cleared by next Friday to avoid extension fees.",
    author: 'Michael Chang (Processor)',
  },
  {
    id: 'act-3',
    type: 'email',
    timestamp: subtractDays(1, 5, 30), // 1 day ago
    summary: 'Action Required: Upload Missing Income Documents',
    details:
      'Hi Sarah, We are reviewing your file and noticed we are still missing your 2023 W-2s. Please upload them to the secure portal at your earliest convenience to keep your loan on track for the target closing date.',
    emailStatus: 'opened',
  },
  {
    id: 'act-4',
    type: 'call',
    timestamp: subtractDays(2, 1, 10), // 2 days ago
    summary: 'Outbound Call - Document Check-in',
    details:
      'Left a voicemail reminding Sarah about the pending W-2s and bank statements.',
    duration: 45, // 45s
    disposition: 'Voicemail',
    isMeaningful: false,
  },
  {
    id: 'act-5',
    type: 'event',
    timestamp: subtractDays(3, 0, 0), // 3 days ago
    summary: 'Appraisal Scheduled',
    details:
      'Appraisal scheduled for the subject property at 123 Oak Street. Appraiser: John Doe Appraisals LLC. Expected turnaround time is 5-7 business days.',
    eventType: 'Property Appraisal',
  },
  {
    id: 'act-6',
    type: 'email',
    timestamp: subtractDays(4, 2, 0), // 4 days ago
    summary: 'Initial Disclosures Sent for eSign',
    details:
      'Your initial loan disclosures have been generated and sent via DocuSign. Please review and sign within 48 hours to proceed with your application.',
    emailStatus: 'opened',
  },
  {
    id: 'act-7',
    type: 'call',
    timestamp: subtractDays(5, 3, 45), // 5 days ago
    summary: 'Initial Consultation & Pre-Approval',
    details:
      'Great initial conversation. Sarah is a first-time homebuyer looking in the Austin area. Pre-approved for $450k conventional, 20% down. Discussed rate options and monthly payment comfort levels.',
    duration: 1845, // 30m 45s
    disposition: 'Connected',
    isMeaningful: true,
  },
]

export const mockMetrics: BorrowerMetrics = {
  lastMeaningfulContact: mockActivities[0].timestamp,
  lastCallDisposition: 'Connected',
  lastCallDuration: 752,
  emailsSent: 2,
  emailsOpened: 2,
}
